if (!customElements.get("image-magnifier")) {
  customElements.define(
    "image-magnifier",
    class ImageMagnifier extends HTMLElement {
      constructor() {
        super();

        // THMV: sem mouse (touch), a lupa não faz sentido (ela depende de
        // mousemove pra acompanhar o cursor) — mas a imagem continua
        // clicável, só que o toque é encaminhado pro botão de zoom em vez
        // de abrir a lupa. Ver onClick().
        this.hasMouse = webvista.hasMouse();

        this.image = this.querySelector("img");
        if (!this.image) return;

        this.isMagnifying = false; // Whether magnifying is active
        this.scale = parseFloat(this.getAttribute("data-scale")) || 1.5; // Zoom scale
      }

      connectedCallback() {
        // THMV: sem mouse (touch), o constructor retorna cedo e nunca
        // atribui this.image — sem essa guarda, o listener era registrado
        // mesmo assim e qualquer toque na imagem quebrava em
        // openMagnifier() com "this.image.cloneNode" undefined.
        if (!this.image) return;

        this.boundOnClick = this.onClick.bind(this);
        this.addEventListener("click", this.boundOnClick);
      }

      disconnectedCallback() {
        if (this.isMagnifying) this.closeMagnifier(); // Close if still open

        if (this.boundOnClick)
          this.removeEventListener("click", this.boundOnClick);

        if (this.boundOnMouseMove)
          this.removeEventListener("mousemove", this.boundOnMouseMove);

        if (this.boundOnMouseLeave)
          this.removeEventListener("mouseleave", this.boundOnMouseLeave);

        if (this.boundOnKeyup)
          document.removeEventListener("keyup", this.boundOnKeyup);
      }

      onClick(event) {
        // Ignore clicks from child interactive elements (e.g., zoom button / modal-opener)
        if (event.target.closest("modal-opener, button, a")) return;

        // THMV: sem mouse, encaminha o toque na imagem pro botão de zoom
        // (mesmo elemento que abre o modal ao ser clicado diretamente),
        // já que a lupa por hover não funciona em touch. O .click() aqui
        // dispara um evento que bolha de volta até este listener, mas o
        // guard acima (closest em "modal-opener") o ignora — sem loop.
        if (!this.hasMouse) {
          this.querySelector(".product-zoom-button")?.click();
          return;
        }

        if (this.isMagnifying) {
          // Close magnifier
          this.closeMagnifier();
        } else {
          // Open magnifier
          this.openMagnifier(event);
        }
      }

      onKeyup(event) {
        if (event.code && event.code.toUpperCase() === "ESCAPE")
          this.closeMagnifier();
      }

      onMouseMove(event) {
        this.updateMagnifierPosition(event);
      }

      onMouseLeave() {
        this.closeMagnifier(); // Close magnifier
      }

      /**
       * Open magnifier
       * @param {MouseEvent} event
       */
      openMagnifier(event) {
        if (this.isMagnifying) return;

        // THMV: guarda defensiva contra this.image ausente/obsoleto (ex.:
        // troca de variante substituiu o <img> no DOM). Tenta reresolver
        // antes de abortar silenciosamente, sem desabilitar o zoom.
        if (!this.image) {
          this.image = this.querySelector("img");
          if (!this.image) return;
        }

        this.magnifier = document.getElementById("Magnifier");
        if (!this.magnifier) {
          this.magnifier = document.createElement("div");
          this.magnifier.id = "Magnifier";
          this.magnifier.className = "magnifier hidden"; // Hidden initially
          this.magnifier.ariaHidden = true; // Hide from assistive tech

          // Append to body
          document.body.appendChild(this.magnifier);
        } else {
          this.magnifier.innerHTML = ""; // Clear for reuse
        }

        // Clone and insert image, ensuring real srcset is used
        this.magnifierImage = this.image.cloneNode(true);
        if (
          this.magnifierImage.dataset.srcset &&
          !this.magnifierImage.getAttribute("srcset")
        ) {
          this.magnifierImage.setAttribute(
            "srcset",
            this.magnifierImage.dataset.srcset,
          );
          this.magnifierImage.removeAttribute("data-srcset");
        }
        this.magnifierImage.removeAttribute("loading");
        this.magnifier.appendChild(this.magnifierImage);

        // Set initial dimensions: magnifier position, image size
        const baseRect = (this.image || this).getBoundingClientRect();
        const targetW = Math.round(baseRect.width * this.scale);
        const targetH = Math.round(baseRect.height * this.scale);
        this.magnifierImage.style.width = `${targetW}px`;
        this.magnifierImage.style.height = `${targetH}px`;

        // Add mouse move listeners
        this.boundOnMouseMove = this.onMouseMove.bind(this);
        this.boundOnMouseLeave = this.onMouseLeave.bind(this);
        this.addEventListener("mousemove", this.boundOnMouseMove);
        this.addEventListener("mouseleave", this.boundOnMouseLeave);

        // Listen for keyboard events
        this.boundOnKeyup = this.onKeyup.bind(this);
        document.addEventListener("keyup", this.boundOnKeyup);

        // Update position
        this.updateMagnifierPosition(event);

        this.isMagnifying = true; // Set state
        this.classList.add("is-magnifying");

        // Show magnifier
        this.magnifier.classList.remove("hidden");
      }

      /**
       * Close magnifier
       * @returns
       */
      closeMagnifier() {
        if (!this.magnifier || !this.isMagnifying) return;

        this.magnifier.classList.add("hidden");
        this.magnifier.innerHTML = ""; // Clear content
        this.isMagnifying = false;
        this.classList.remove("is-magnifying");

        // Remove mouse move listener
        if (this.boundOnMouseMove) {
          this.removeEventListener("mousemove", this.boundOnMouseMove);
          this.boundOnMouseMove = null; // Prevent duplicate removal
        }

        if (this.boundOnMouseLeave) {
          this.removeEventListener("mouseleave", this.boundOnMouseLeave);
          this.boundOnMouseLeave = null; // Prevent duplicate removal
        }

        if (this.boundOnKeyup) {
          document.removeEventListener("keyup", this.boundOnKeyup);
          this.boundOnKeyup = null;
        }
      }

      /**
       * Update magnifier position
       * @param {MouseEvent} event
       * @returns
       */
      updateMagnifierPosition(event) {
        if (!this.magnifier || !this.magnifierImage) return;

        const rect = this.getBoundingClientRect(); // Container bounds

        const magnifyPositionX = event.clientX; // Magnifier X position
        const magnifyPositionY = event.clientY; // Magnifier Y position
        const imageOffsetX = (magnifyPositionX - rect.left) * this.scale; // Image X offset
        const imageOffsetY = (magnifyPositionY - rect.top) * this.scale; // Image Y offset

        this.magnifier.style.insetInlineStart = `${magnifyPositionX}px`;
        this.magnifier.style.insetBlockStart = `${magnifyPositionY}px`;
        this.magnifierImage.style.transform = `translate(-${imageOffsetX}px, -${imageOffsetY}px)`;
      }
    },
  );
}
